import type { UIViewInjectedProps } from '@uirouter/react';
import { Button, message, PageHeader } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Table } from '@/components/table';
import { getGenericFilters, toQueryFormat } from '@/components/table/utils';
import { PERMISSION_CREATE, PERMISSION_OBJ_DOCUMENT_IMPORT } from '@/constants/permissions';
import { authStore, documentImportStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';
import { isAppReloading } from '@/utils/reload';

import { askToConfirm } from './components/ask-to-confirm';
import { MultiWaybillDragger } from './components/multi-waybill-dragger';
import { WaybillPdfDragger } from './components/waybill-pdf-dragger';
import { ReviewDrawer } from './review-drawer';
import { columns, type DisplayRow, getColumns, isActive, isLocalRow, type LocalUploadRow } from './setup';
import { splitFailureMessageId } from './split-pdf';
import {
  acquireUploadSlot,
  hasPendingSplits,
  hasPendingUploads,
  hasRetryableRows,
  isRetryableUploadError,
  LATE_TOAST_SECONDS,
  mayUploadAs,
  nextLocalId,
  releaseUploadSlot,
  shouldConfirmLeave,
  shouldWarnOnUnload,
} from './upload-queue';

const WAYBILL_IMPORTS_STATE = 'base-layout.waybill-imports';

// Rows of the mounted page, null while it is not mounted.
let mountedPageRows: { current: LocalUploadRow[] } | null = null;

// Module scope and never removed, like the queue it guards: uploads and splits outlive the page.
const warnBeforeUnload = (e: BeforeUnloadEvent) => {
  const warn = shouldWarnOnUnload({
    authorized: authStore.isAuthorized(),
    reloading: isAppReloading(),
    pendingUploads: hasPendingUploads(),
    pendingSplits: hasPendingSplits(),
    retryableRows: hasRetryableRows(mountedPageRows?.current ?? []),
  });
  if (!warn) return;
  // Shows the browser's own "Leave site?" dialog; no custom text is possible.
  e.preventDefault();
  // Legacy: Chrome/Edge < 119 show it only when returnValue is set.
  e.returnValue = true;
};

const POLL_INTERVAL_MS = 5000;
// Sticky filters that ride along with every list query. The page is dedicated
// to waybill_pdf imports — other doc-import types should not show here.
const stickyFilters: cyrian.api.documentImports.GetParams = {
  type: ['waybill_pdf'],
};

export const WaybillImports = observer(({ transition }: UIViewInjectedProps) => {
  const [loading, setLoading] = useState(false);
  // Files dropped but not yet persisted by the backend. Three states:
  //   - 'queued' — added to the table on drop, still waiting for a semaphore
  //     slot (no network activity yet). Operator sees the file is registered.
  //   - 'uploading' — slot acquired, POST in flight.
  //   - 'upload_failed' — POST rejected (or the PDF could not be split); row
  //     stays with the error, Retry if it still holds the file, and Remove.
  //     (Successful uploads are removed here and the server-returned row is
  //     prepended to the store.)
  // Component-scope state by design: a refresh wipes them (they aren't
  // persisted anywhere), the beforeunload guard catches that case.
  const [localUploads, setLocalUploads] = useState<LocalUploadRow[]>([]);
  // Currently-open Review drawer target — just the row id. The live row is
  // derived from `documentImportStore.items` on every render so a refetch
  // (e.g. after a failed Submit) flows reactively into the drawer through
  // MobX's `observer`, instead of needing manual setReviewing(updatedRow)
  // bookkeeping.
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  // Id of the row whose force-reparse POST is in flight. Renders a loading
  // spinner on the per-row button and disables Popconfirm so a double click
  // can't fire a second POST before fetchList settles into the new pending row.
  const [forceReparsingId, setForceReparsingId] = useState<number | null>(null);
  // Re-apply pushes the stored parsed_data back into the linked waybill —
  // no OCR call. Loading state mirrors force-reparse: per-row spinner +
  // disabled Popconfirm to block a double-click before fetchList settles.
  const [reapplyingId, setReapplyingId] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cancel flag for the polling chain: cleared on unmount, checked inside the
  // async timer callback before scheduling the next tick or touching state.
  // Without it, an in-flight `fetchList()` resolves after unmount and both
  // (a) re-arms the timer, leaking it forever, and (b) calls setLoading(false)
  // on a torn-down component (React warning).
  const cancelledRef = useRef(false);

  // The Table wrapper auto-pulls pagination total from store.count and
  // pageSize from settingsStore.itemsPerPage. We pass an onChange handler
  // that re-applies the sticky `type` filter so paging/sorting/filtering
  // doesn't drop the waybill_pdf-only constraint. genericFilters is empty
  // today (no sortable/filterable columns), but kept for parity with the
  // project pattern (waybills, attachment-history, lots, ...).
  const genericFilters = useMemo(() => getGenericFilters(columns), []);

  // `silent` skips the loading spinner — used for background polling so the
  // table doesn't flash a spinner every 5 s while jobs are still pending.
  //
  // Silent polls also have to bypass `documentImportStore.forceLoad`, which
  // has `.catch(showError)` baked in (collection.ts:214). That call pops a
  // toast for *every* error before any consumer-side catch sees it — so the
  // operator would see a transient "timeout of 60000ms exceeded" toast from
  // a poll that the next 5-second tick usually clears on its own. Going
  // through the API directly + manual `fillStore` keeps the error in our
  // catch where we can downgrade it to a console warning.
  const fetchList = useCallback(async (silent = false) => {
    if (silent) {
      try {
        const res = await api.documentImports.getAll(stickyFilters).source;
        if (res?.data) {
          documentImportStore.fillStore(res.data.items, res.data.count);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[imports] silent poll failed, will retry', err);
      }
      return;
    }
    // User-initiated load — keep the store's built-in toast on error.
    setLoading(true);
    try {
      await documentImportStore.forceLoad(stickyFilters);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  const onChangeTable = useCallback(
    (...args: Parameters<typeof toQueryFormat> extends [unknown, ...infer R] ? R : never[]) => {
      const params = (toQueryFormat as any).apply(null, [genericFilters, ...args]);
      documentImportStore.forceLoad({
        ...stickyFilters,
        ...params,
      } as cyrian.api.documentImports.GetParams);
    },
    [genericFilters],
  );

  // Lightweight polling: refresh silently while there are still active jobs.
  const schedulePoll = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;
      if (documentImportStore.items.some((i) => isActive(i.status))) {
        await fetchList(true);
      }
      if (cancelledRef.current) return;
      schedulePoll();
    }, POLL_INTERVAL_MS);
  }, [fetchList]);

  useEffect(() => {
    cancelledRef.current = false;
    void fetchList();
    schedulePoll();
    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchList, schedulePoll]);

  // Read by handlers that must not wait for a re-render (unload/leave guards, Retry).
  const localUploadsRef = useRef<LocalUploadRow[]>([]);
  useEffect(() => {
    localUploadsRef.current = localUploads;
  }, [localUploads]);

  // Refresh / tab-close guard; see `shouldWarnOnUnload` for what counts as unsent work.
  useEffect(() => {
    // The same function on every mount, so the browser keeps a single listener.
    window.addEventListener('beforeunload', warnBeforeUnload);
    mountedPageRows = localUploadsRef;
    return () => {
      mountedPageRows = null;
    };
  }, []);

  // In-app navigation unmounts the page and drops failed rows, which beforeunload does not see.
  const declineLeaveQuestionRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const deregister = transition.router.transitionService.onStart({ exiting: WAYBILL_IMPORTS_STATE }, (trans) => {
      const confirm = shouldConfirmLeave({
        authorized: authStore.isAuthorized(),
        retryableRows: hasRetryableRows(localUploadsRef.current),
        // Not to().name: a role without access redirects the reload to access-denied first.
        reloadsPage: !!trans.options().reload,
      });
      if (!confirm) return true;
      // A newer transition (e.g. browser Back while the question is open) supersedes the one still asking.
      declineLeaveQuestionRef.current?.();
      const { answer, decline } = askToConfirm({
        title: getTranslatedString('waybills.imports.leave-confirm.title'),
        content: getTranslatedString('waybills.imports.leave-confirm.content'),
        okText: getTranslatedString('waybills.imports.leave-confirm.ok'),
        cancelText: getTranslatedString('waybills.imports.leave-confirm.cancel'),
      });
      declineLeaveQuestionRef.current = decline;
      return answer.finally(() => {
        if (declineLeaveQuestionRef.current === decline) declineLeaveQuestionRef.current = null;
      });
    });
    return () => {
      deregister();
    };
  }, [transition]);

  // Mounted ref to skip setState in async callbacks that resolve after the
  // operator has navigated away. Without it, an upload that returns post-
  // unmount triggers a "Can't perform a React state update on an unmounted
  // component" warning in React 16 dev builds.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cancellation signals for queued rows that the operator clicks Remove on.
  // We have to persist the signal across the `await acquireUploadSlot()` wait
  // (component state is captured by closure at handler entry), so a Set in a
  // ref is the simplest mechanism. The set's tombstones are cleared inline by
  // the consumer in `enqueueUpload`.
  const cancelledIdsRef = useRef<Set<string>>(new Set());

  const removeLocalUpload = useCallback((localId: string) => {
    if (!mountedRef.current) return;
    setLocalUploads((prev) => {
      // If the row is still 'queued' (no POST in flight yet), flag it as
      // cancelled so `enqueueUpload` bails when its slot finally resolves
      // instead of sending the file anyway. For 'upload_failed' rows the
      // upload has already settled, so this only drops the row (and its File).
      const row = prev.find((u) => u._localId === localId);
      if (row && row._localStatus === 'queued') {
        cancelledIdsRef.current.add(localId);
      }
      return prev.filter((u) => u._localId !== localId);
    });
  }, []);

  // `splitFrom`: the multi-waybill PDF this page came from. `silent`: no per-file success toast (one per batch).
  const enqueueUpload = useCallback(
    async (file: File, { splitFrom, silent = false }: { splitFrom?: string; silent?: boolean } = {}) => {
      // Add the row immediately — *before* acquireUploadSlot — as 'queued',
      // so files waiting in the semaphore are visible too. Without this, only
      // the 3 actually in flight would show in the table; the other 17 of a
      // 20-file drop would be invisible until each one's turn came.
      const localId = nextLocalId();
      const ownerId = authStore.profile?.id;
      if (mountedRef.current) {
        setLocalUploads((prev) => [
          { _localId: localId, fileName: file.name, _localStatus: 'queued', _splitFrom: splitFrom },
          ...prev,
        ]);
      }

      await acquireUploadSlot();

      if (!mayUploadAs({ authorized: authStore.isAuthorized(), ownerId, userId: authStore.profile?.id })) {
        releaseUploadSlot();
        message.warning({
          key: 'imports-not-sent-signed-out',
          content: getTranslatedString('waybills.imports.upload.not-sent-signed-out'),
          duration: LATE_TOAST_SECONDS,
        });
        return;
      }

      // The operator may have clicked Remove on this row while it was queued
      // (the signal persists in cancelledIdsRef across the async wait). Bail
      // out before sending the file — release the slot so the next waiter
      // takes our place, and return early without a POST.
      if (cancelledIdsRef.current.has(localId)) {
        cancelledIdsRef.current.delete(localId);
        releaseUploadSlot();
        return;
      }

      // Slot acquired — now actually uploading. Flip the status so the
      // operator can distinguish "waiting in queue" from "network active".
      if (mountedRef.current) {
        setLocalUploads((prev) => prev.map((u) => (u._localId === localId ? { ...u, _localStatus: 'uploading' } : u)));
      }

      try {
        await api.documentImports.submitWaybillPdf(file).source;
        // Don't optimistically prepend res.data to the store: a concurrent
        // polling forceLoad that's already in flight can resolve afterwards
        // and overwrite the prepend with stale state, and CollectionStore's
        // fillStore also clobbers the server-side `count` when called without
        // a count argument. Drop the local row and trigger a fresh fetch —
        // the GET is the source of truth and serializes after any in-flight
        // polling fetch in practice (last-resolved wins).
        removeLocalUpload(localId);
        void fetchList(true);
        if (!silent) {
          message.success(getTranslatedString('waybills.imports.upload.queued', { name: file.name }));
        }
      } catch (err) {
        // Keep the row visible as `upload_failed` with the error message so the
        // operator sees which file went wrong and can dismiss it via Remove.
        // Backend errors can arrive either as a string or as an array of
        // strings (see `showError` in src/utils/common.tsx) — normalize.
        const rawMsg = (err as any)?.response?.data?.error?.message ?? (err as any)?.message;
        const errorMsg = Array.isArray(rawMsg)
          ? rawMsg.join(' ')
          : (rawMsg ?? getTranslatedString('waybills.imports.upload.failed-generic'));
        // Keep the File (and so Retry) only when a second try can succeed.
        const retryFile = isRetryableUploadError(err) ? file : undefined;
        if (mountedRef.current) {
          setLocalUploads((prev) =>
            prev.map((u) =>
              u._localId === localId ? { ...u, _localStatus: 'upload_failed', _error: errorMsg, _file: retryFile } : u,
            ),
          );
        } else if (splitFrom) {
          // The row went with the page. One toast per batch, updated by each failing page.
          message.error({
            key: `imports-split-failed:${splitFrom}`,
            content: getTranslatedString('waybills.imports.split.pages-failed-after-leave', {
              name: splitFrom,
              error: errorMsg,
            }),
            duration: LATE_TOAST_SECONDS,
          });
        } else {
          message.error(
            getTranslatedString('waybills.imports.upload.failed-after-leave', { name: file.name, error: errorMsg }),
            LATE_TOAST_SECONDS,
          );
        }
      } finally {
        releaseUploadSlot();
      }
    },
    [fetchList, removeLocalUpload],
  );

  // Ids already retried: a second click must not send the file twice.
  const retriedIdsRef = useRef<Set<string>>(new Set());

  const retryLocalUpload = useCallback(
    (localId: string) => {
      if (retriedIdsRef.current.has(localId)) return;
      const row = localUploadsRef.current.find((u) => u._localId === localId);
      if (!row?._file) return;
      retriedIdsRef.current.add(localId);
      removeLocalUpload(localId);
      void enqueueUpload(row._file, { splitFrom: row._splitFrom });
    },
    [enqueueUpload, removeLocalUpload],
  );

  const onSplit = useCallback(
    (file: File, pages: File[]) => {
      if (pages.length === 1) {
        void enqueueUpload(pages[0]);
        return;
      }
      message.success(getTranslatedString('waybills.imports.split.done', { name: file.name, count: pages.length }));
      // React 16 does not batch updates outside event handlers: one render for all queued rows.
      unstable_batchedUpdates(() => {
        for (const page of pages) void enqueueUpload(page, { splitFrom: file.name, silent: true });
      });
    },
    [enqueueUpload],
  );

  // No `_file` on this row: nothing reached the server, so there is nothing to retry.
  const onSplitFailed = useCallback((file: File, err: unknown) => {
    const error = getTranslatedString(splitFailureMessageId(err));
    if (!mountedRef.current) {
      message.error(`${file.name}: ${error}`, LATE_TOAST_SECONDS);
      return;
    }
    setLocalUploads((prev) => [
      { _localId: nextLocalId(), fileName: file.name, _localStatus: 'upload_failed', _error: error },
      ...prev,
    ]);
  }, []);

  // Build the columns once per `removeLocalUpload` identity (effectively
  // once, since useCallback gives it a stable reference). The factory needs
  // the page-owned remove callback because client-only state shouldn't leak
  // into the shared documentImportStore, and a page-owned onReview because
  // the drawer is page-scoped state.
  const onReview = useCallback((row: cyrian.api.documentImports.DocumentImport) => {
    setReviewingId(row.id);
  }, []);
  // Force re-parse inserts a fresh pending row that takes over the S3 PDF; the
  // clicked row is kept, re-pointed at it and refreshed once the new parse
  // persists. Close the Review drawer if it was open on it — the row no longer
  // owns the PDF, so its Submit (retry) is refused and points at the new import.
  const onForceReparse = useCallback(
    async (id: number) => {
      setForceReparsingId(id);
      try {
        await api.documentImports.forceReparse(id).source;
        if (reviewingId === id) setReviewingId(null);
        // Await the refresh so the per-row spinner stays up until the new
        // pending row appears in the store. Without await the spinner would
        // clear as soon as the POST returns — nothing in the list signals
        // the click went through yet and an operator could fire a second one.
        await fetchList(true);
      } catch (err) {
        showError(err);
      } finally {
        setForceReparsingId(null);
      }
    },
    [fetchList, reviewingId],
  );
  const onReapply = useCallback(
    async (id: number) => {
      setReapplyingId(id);
      try {
        await api.documentImports.reapply(id).source;
        await fetchList(true);
      } catch (err) {
        showError(err);
      } finally {
        setReapplyingId(null);
      }
    },
    [fetchList],
  );
  // Derive the live row on each render so refetches and silent polls keep
  // the drawer's `importRow` prop fresh without manual sync.
  const reviewingRow =
    reviewingId != null ? (documentImportStore.items.find((i) => i.id === reviewingId) ?? null) : null;
  const tableColumns = useMemo(
    () =>
      getColumns({
        onRemoveLocal: removeLocalUpload,
        onRetryLocal: retryLocalUpload,
        onReview,
        onForceReparse,
        onReapply,
        forceReparsingId,
        reapplyingId,
      }),
    [removeLocalUpload, retryLocalUpload, onReview, onForceReparse, onReapply, forceReparsingId, reapplyingId],
  );

  return (
    <>
      <PageHeader
        title={getTranslatedString('waybills.imports.title')}
        subTitle={getTranslatedString('waybills.imports.subtitle')}
        onBack={() => transition.router.stateService.go('base-layout.waybills')}
        style={{ padding: 0, marginBottom: 16 }}
        extra={[
          <Button key="refresh" onClick={() => void fetchList()} loading={loading}>
            <FormattedMessage id="waybills.imports.refresh" />
          </Button>,
        ]}
      />

      {/* Wrap in a sized container: Antd's `.ant-upload-drag` has `height:100%`,
          and our parent `.content` has `height:100%` too — without this wrapper
          the dropzone stretches to the full viewport height. The grid row gives
          it a content-sized box: at least 120 px, taller when the text wraps (FR). */}
      {/* Uploads need Create (the POST endpoint's guard); read-only users would only collect 403 rows. */}
      {authStore.hasPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_CREATE]]) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gridTemplateRows: 'minmax(120px, auto)',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <WaybillPdfDragger onFile={enqueueUpload} />
          <MultiWaybillDragger onSplit={onSplit} onSplitFailed={onSplitFailed} />
        </div>
      )}

      <Table
        rowKey={(r: DisplayRow) => (isLocalRow(r) ? r._localId : String(r.id))}
        columns={tableColumns}
        // Inline spread (not useMemo): documentImportStore.items is an
        // observable MobX array with stable identity across in-place mutations
        // — useMemo with that dep would never recompute even when items change.
        // Recomputing every render is cheap (≤200 elements typically).
        dataSource={[...localUploads, ...documentImportStore.items]}
        store={documentImportStore}
        loading={loading}
        onChange={onChangeTable}
        size="middle"
      />

      <ReviewDrawer
        importRow={reviewingRow}
        onClose={() => setReviewingId(null)}
        onSubmitted={() => void fetchList(true)}
        onRefresh={() => fetchList(true)}
      />
    </>
  );
});
